import axios, { AxiosResponse } from 'axios'
import { useState, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { useTranslation, TFunction } from 'react-i18next'
import { encryptAES } from '../../util/cipher'
import { validateISYL } from '../../util/validate'
import { clearProfileData, setProfile } from '../../redux/action/profile'
import useLoadingStore from '../../stores/useLoadingStore'
import ConfigAPI from '../../constant/ConfigAPI'

export type Response = { result?: AxiosResponse<any>; error?: any }

export interface Form {
    oldPassword?: string
    newPassword?: string
    confirmNewPassword?: string
}

export const validate = async (values: Form, t: TFunction<'translation'>) => {
    const errors: Partial<Form> = {}
    if (!values.oldPassword) {
        errors.oldPassword = `${t('LABEL.TEXT_REQUIRED')}`
    }
    if (!values.newPassword) {
        errors.newPassword = `${t('LABEL.TEXT_REQUIRED')}`
    }
    if (values.newPassword && !validateISYL(encryptAES(values.newPassword))) {
        errors.newPassword = `${t('VALIDATE.MESSAGES.PASSWORD')}`
    }
    if (!values.confirmNewPassword) {
        errors.confirmNewPassword = `${t('LABEL.TEXT_REQUIRED')}`
    }
    if (values.confirmNewPassword && values.confirmNewPassword !== values.newPassword) {
        errors.confirmNewPassword = `${t('VALIDATE.MESSAGES.CONFIRM_PASSWORD')}`
    }
    return errors
}

const useChangeISYL = () => {
    const valueSubmitted = useRef<Form>()
    const [openDialogOk, setOpenDialogOk] = useState<boolean>(false)
    const [openDialogError, setOpenDialogError] = useState<boolean>(false)
    const [statusCode, setStatusCode] = useState<number>()
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const history = useHistory()
    const loadingStore = useLoadingStore()
    const [alertMessageOk, setAlertMessageOk] = useState<string>('')
    const [alertMessageError, setAlertMessageError] = useState<string>('')

    const callService = async (data: Form) => {
        try {
            loadingStore.setLoading(true)
            const url = `${process.env.REACT_APP_API}/api/users/changePassword`
            const reqParams = {
                oldPassword: encryptAES(data.oldPassword as string),
                newPassword: encryptAES(data.newPassword as string),
                confirmNewPassword: encryptAES(data.confirmNewPassword as string),
            }
            const result = await axios.post(url, reqParams, ConfigAPI)
            if (result.status === 200) {
                if (result.data.message === 'success') {
                    setStatusCode(200.1)
                    setOpenDialogOk(true)
                    setAlertMessageOk(t('ALERT.CONTENT.CHANGE_PASSWORD_SUCCESS'))
                } else {
                    setStatusCode(200.2)
                    setOpenDialogOk(true)
                    setAlertMessageOk(t('ALERT.CONTENT.OLD_PASSWORD_INCORRECT'))
                }
            }
            return { result }
        } catch (error) {
            //@ts-ignore
            const errMsg: string = error?.toString()
            if (errMsg) {
                if (errMsg.includes('401')) {
                    //reLoginForTokenExpired()
                    //callServiceReLogin()
                } else {
                    setStatusCode(500)
                    setOpenDialogError(true)
                    setAlertMessageError(t('ALERT.CONTENT.CHANGE_PASSWORD_NOT_SUCCESS'))
                }
            }
            return { error }
        } finally {
            loadingStore.setLoading(false)
        }
    }

    const handleSubmitForm = (values: any) => {
        valueSubmitted.current = values
        callService(valueSubmitted.current as Form)
    }

    const handleClickOk = () => {
        setOpenDialogOk(false)
        setOpenDialogError(false)
        if (statusCode !== 200.2) {
            if (statusCode === 401) {
                history.push('/login')
            } else if (statusCode !== 500) {
                dispatch(clearProfileData())
                globalThis.sessionStorage.clear()
                globalThis.localStorage.removeItem(`${process.env.REACT_APP_TOKEN_PROFILE}`)
                history.push('/')
            }
        }
    }

    return {
        onSubmitForm: handleSubmitForm,
        onClickOk: handleClickOk,
        openDialogOk,
        alertMessageOk,
        openDialogError,
        alertMessageError,
    }
}

export default useChangeISYL
